import type { FormEvent } from 'react'
import { useState } from 'react'
import { filterChipTriggerClasses } from '@/components/shared/filter-bar-styles'
import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAccountType } from '@/hooks/useAccountType'
import { useModuleViews } from '@/hooks/useModuleViews'
import { useProjectTaskViews } from '@/hooks/useProjectTaskViews'
import { useViewUrlState } from '@/hooks/useViewUrlState'
import { cn } from '@/lib/utils'
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CheckIcon,
  FloppyDiskIcon,
  LinkIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import {
  ModuleTypeEnum,
  PreferenceScopeEnum,
  ViewConfig,
} from '@scrambl/shared'

interface ViewSelectorProps {
  moduleType: ModuleTypeEnum
  canManage?: boolean
  getCurrentConfig: () => ViewConfig | null
  applyConfig: (config: ViewConfig) => Promise<void> | void
  className?: string
  /** Optional project ID for project-scoped views (used for project tasks) */
  projectId?: string
  /**
   * Saved-view surface for an embedded list (`SAVED_VIEW_SURFACES.*` or
   * `collection:<id>`), so it keeps its own views instead of sharing the
   * module's top-level page. Ignored when `projectId` is set.
   */
  surface?: string | null
  /**
   * ScQL currently in the surface's query bar, for pages that have one
   * (tickets today). Saving a view stores this on the SAME record as the
   * column/grouping config, so a view is "this query + this layout".
   */
  currentQuery?: string
  /** Applies a saved view's query back into the query bar */
  onApplyQuery?: (query: string) => void
}

const sanitizeViewConfig = (config: ViewConfig | null): ViewConfig | null => {
  if (!config) return null
  const filters = config.filters ? { ...config.filters } : undefined
  if (filters && 'page' in filters) {
    delete (filters as Record<string, unknown>).page
  }
  return { ...config, filters }
}

const sortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortValue)
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortValue(record[key])
        return acc
      }, {})
  }
  return value
}

const stableStringify = (value: unknown): string => {
  return JSON.stringify(sortValue(value))
}

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

const normalizeConfig = (
  config: ViewConfig | null | undefined,
): ViewConfig | null => {
  if (!config) return null
  const normalized: ViewConfig = {}
  if (config.filters && !isEmptyValue(config.filters)) {
    normalized.filters = config.filters
  }
  if (config.columnFilters && !isEmptyValue(config.columnFilters)) {
    normalized.columnFilters = config.columnFilters
  }
  if (config.columnVisibility && !isEmptyValue(config.columnVisibility)) {
    normalized.columnVisibility = config.columnVisibility
  }
  if (config.columnOrder && !isEmptyValue(config.columnOrder)) {
    normalized.columnOrder = config.columnOrder
  }
  if (config.columnWidths && !isEmptyValue(config.columnWidths)) {
    normalized.columnWidths = config.columnWidths
  }
  if (
    config.groupBy !== undefined &&
    config.groupBy !== null &&
    config.groupBy !== ''
  ) {
    normalized.groupBy = config.groupBy
  }
  // Return null if the normalized config is empty (no meaningful differences)
  if (Object.keys(normalized).length === 0) {
    return null
  }
  return normalized
}

/**
 * Comparing a saved view against the live surface is not a plain deep-equal.
 * The table publishes more state than a view records (every column's
 * visibility, order and width, plus filter keys a store gained after the view
 * was saved), so a literal diff reports "Edited" on views nobody touched.
 *
 * `''`, `[]`, `{}`, `null`, `undefined` and `false` all describe the same
 * thing on a list surface: nothing is filtering by that key. They compare
 * equal, so a filter key present-but-empty on one side never counts as an
 * edit.
 */
const isBlankValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === false) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/**
 * Filters and column filters are the user's own intent, so every key on
 * either side counts — but only once blanks are folded together.
 */
const areFilterMapsEqual = (current: unknown, saved: unknown): boolean => {
  const currentMap = asRecord(current)
  const savedMap = asRecord(saved)
  const keys = new Set([...Object.keys(currentMap), ...Object.keys(savedMap)])
  for (const key of keys) {
    const currentValue = currentMap[key]
    const savedValue = savedMap[key]
    if (isBlankValue(currentValue) && isBlankValue(savedValue)) continue
    if (stableStringify(currentValue) !== stableStringify(savedValue)) {
      return false
    }
  }
  return true
}

/**
 * Column layout is different: a view either captured it or it did not, and
 * the table only publishes it once columns and preferences have loaded. A
 * blank on either side means "this view does not track the layout" (or "the
 * table has not reported one yet") — never a user edit. When both sides have
 * one, only the columns the view actually recorded are compared, so a column
 * added to the module afterwards does not dirty every older view, and a
 * column the view remembers but the module has since dropped is skipped.
 */
const areLayoutMapsEqual = (current: unknown, saved: unknown): boolean => {
  if (isBlankValue(current) || isBlankValue(saved)) return true
  const currentMap = asRecord(current)
  const savedMap = asRecord(saved)
  return Object.keys(savedMap).every(
    key =>
      // A column the module no longer has (a retired standard column, a
      // deleted custom field) is not on screen to be edited.
      !(key in currentMap) ||
      stableStringify(currentMap[key]) === stableStringify(savedMap[key]),
  )
}

/**
 * Column order is stored as a per-column index, and those indexes shift
 * wholesale when a column is added or removed — re-showing a hidden column
 * appends it to the end of the map, for instance. Only the relative order of
 * the columns on screen is meaningful, so hidden columns and columns the view
 * never recorded are left out of the comparison.
 */
const isColumnOrderEqual = (
  current: unknown,
  saved: unknown,
  currentVisibility: unknown,
  savedVisibility: unknown,
): boolean => {
  if (isBlankValue(current) || isBlankValue(saved)) return true
  const currentMap = asRecord(current)
  const savedMap = asRecord(saved)
  const currentVisible = asRecord(currentVisibility)
  const savedVisible = asRecord(savedVisibility)
  const isShown = (key: string) =>
    currentVisible[key] !== false && savedVisible[key] !== false
  const sharedKeys = Object.keys(savedMap).filter(
    key => key in currentMap && isShown(key),
  )
  const byIndex = (map: Record<string, unknown>) =>
    [...sharedKeys].sort((a, b) => Number(map[a] ?? 0) - Number(map[b] ?? 0))
  return (
    stableStringify(byIndex(currentMap)) === stableStringify(byIndex(savedMap))
  )
}

/** Exported for tests: the whole false-"Edited" bug lived in this compare. */
export const isConfigModified = (
  current: ViewConfig | null,
  saved: ViewConfig | null,
): boolean => {
  // Nothing published yet (the table is still mounting) is not an edit.
  if (!current || normalizeConfig(current) === null) return false
  if (!saved) return false
  if (!areFilterMapsEqual(current.filters, saved.filters)) return true
  if (!areFilterMapsEqual(current.columnFilters, saved.columnFilters)) {
    return true
  }
  if (!areLayoutMapsEqual(current.columnVisibility, saved.columnVisibility)) {
    return true
  }
  if (!areLayoutMapsEqual(current.columnWidths, saved.columnWidths)) return true
  if (
    !isColumnOrderEqual(
      current.columnOrder,
      saved.columnOrder,
      current.columnVisibility,
      saved.columnVisibility,
    )
  ) {
    return true
  }
  if (isBlankValue(current.groupBy) && isBlankValue(saved.groupBy)) return false
  return current.groupBy !== saved.groupBy
}

const VIEW_SCOPE_DETAILS: Record<
  PreferenceScopeEnum,
  {
    label: string
    description: string
    Icon: typeof UserIcon
    color: string
    bgColor: string
  }
> = {
  [PreferenceScopeEnum.USER]: {
    label: 'Personal view',
    description: 'Only you can see this view.',
    Icon: UserIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  [PreferenceScopeEnum.ORGANIZATION]: {
    label: 'Shared view',
    description: 'Visible to everyone in your organization.',
    Icon: UsersIcon,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
}

export function ViewSelector({
  moduleType,
  canManage = false,
  getCurrentConfig,
  applyConfig,
  className,
  projectId,
  surface,
  currentQuery,
  onApplyQuery,
}: ViewSelectorProps) {
  const { isPersonal } = useAccountType()
  // Use project-scoped views when projectId is provided, otherwise use module-level views
  const moduleViews = useModuleViews({ moduleType, surface, canManage })
  const projectViews = useProjectTaskViews({
    projectId: projectId || '',
    canManage,
    enabled: !!projectId,
  })

  // Select the appropriate views based on whether projectId is provided
  const isProjectScoped = !!projectId
  const {
    views,
    userViews,
    activeView,
    activeViewId,
    isLoading,
    setActiveViewId,
    saveActiveView,
    createView,
    deleteView,
  } = isProjectScoped ? projectViews : moduleViews

  // Handle the difference in naming between module views (orgViews) and project views (sharedViews)
  const orgViews = isProjectScoped
    ? projectViews.sharedViews
    : moduleViews.orgViews
  const orgDefaultViewId = isProjectScoped
    ? projectViews.defaultViewId
    : moduleViews.orgDefaultViewId
  const setOrgDefaultViewId = isProjectScoped
    ? projectViews.setDefaultViewId
    : moduleViews.setOrgDefaultViewId

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [pendingScope, setPendingScope] = useState<PreferenceScopeEnum>(
    PreferenceScopeEnum.USER,
  )
  const [viewName, setViewName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isSwitchDialogOpen, setIsSwitchDialogOpen] = useState(false)
  const [pendingSwitchViewId, setPendingSwitchViewId] = useState<string | null>(
    null,
  )
  const [isSwitching, setIsSwitching] = useState(false)
  // Any view can be deleted from its row, not only the active one.
  const [pendingDeleteViewId, setPendingDeleteViewId] = useState<string | null>(
    null,
  )
  const [isDeleting, setIsDeleting] = useState(false)
  // While a saved view is being written into the table, the surface is in
  // between states and comparing it against the view is meaningless. This is
  // a bounded window (it closes when `applyConfig` resolves) rather than a
  // "until something changes" heuristic, which used to clear on the first
  // unrelated re-render and made the badge look like it was triggered by
  // opening this menu.
  const [isApplyingView, setIsApplyingView] = useState(false)

  const currentConfigRaw = getCurrentConfig()
  const currentConfig = sanitizeViewConfig(currentConfigRaw)
  const hasConfig = !!currentConfig
  const scopeDetails = VIEW_SCOPE_DETAILS[pendingScope]
  const ScopeIcon = scopeDetails.Icon
  const isNameValid = viewName.trim().length > 0
  const isModified = (() => {
    if (isApplyingView) return false
    if (!activeView) {
      return false
    }
    // The query is part of the artifact on surfaces that carry one, so it is
    // checked before the config short-circuits on "both empty".
    const isQueryModified =
      !!onApplyQuery &&
      (currentQuery ?? '').trim() !== (activeView.query ?? '').trim()
    if (isQueryModified) return true

    return isConfigModified(
      currentConfig,
      sanitizeViewConfig(activeView.config),
    )
  })()

  // A shared, unmodified record view is addressable by id, so the link stays
  // short and keeps tracking the view if its owner edits it later. Personal
  // views and unsaved edits are shared as a self-contained inline payload —
  // a colleague can't open someone else's PRIVATE row.
  const shareableSavedViewId =
    activeView?.source === 'record' &&
    activeView.scope === PreferenceScopeEnum.ORGANIZATION &&
    !isModified
      ? activeView.id
      : null

  const { getShareUrl } = useViewUrlState({
    moduleType,
    surface,
    getCurrentConfig,
    applyConfig,
    preferSavedViewId: shareableSavedViewId,
    viewName: activeView?.name,
  })

  const handleCopyShareLink = async () => {
    const url = getShareUrl()
    if (!url) {
      toast.error('There is nothing to share in this view yet')
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied — anyone on your team can open this view')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  const canDeleteView = (view: { scope: PreferenceScopeEnum }) =>
    view.scope === PreferenceScopeEnum.USER || canManage
  // A member editing a shared view cannot write to it; `saveActiveView` would
  // fork it silently, so the menu offers "Save a copy" instead of "Save".
  const canSaveActiveView = Boolean(
    activeView && (activeView.scope === PreferenceScopeEnum.USER || canManage),
  )

  const applyViewQuery = (view: { query?: string | null }) => {
    if (!onApplyQuery) return
    onApplyQuery(view.query ?? '')
  }

  const handleSaveCurrent = async () => {
    const config = sanitizeViewConfig(getCurrentConfig())
    if (!config) return
    await saveActiveView(
      config,
      onApplyQuery ? (currentQuery ?? '') : undefined,
    )
  }

  const handleRevertChanges = async () => {
    if (!activeView) return
    setIsApplyingView(true)
    try {
      applyViewQuery(activeView)
      await applyConfig(activeView.config)
    } finally {
      setIsApplyingView(false)
    }
    setOpen(false)
  }

  const handleOpenCreateDialog = (scope: PreferenceScopeEnum) => {
    setPendingScope(scope)
    setViewName('')
    setIsCreateDialogOpen(true)
  }

  const handleSetDefault = async (view: {
    id: string
    scope: PreferenceScopeEnum
  }) => {
    if (view.scope !== PreferenceScopeEnum.ORGANIZATION) return
    await setOrgDefaultViewId(view.id)
  }

  const handleDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open)
    if (!open) {
      setViewName('')
      setIsSaving(false)
    }
  }

  const handleCreateSubmit = async (event?: FormEvent) => {
    event?.preventDefault()
    const trimmedName = viewName.trim()
    if (!trimmedName) return
    const config = sanitizeViewConfig(getCurrentConfig())
    if (!config) return
    setIsSaving(true)
    try {
      await createView(
        trimmedName,
        config,
        pendingScope,
        onApplyQuery ? (currentQuery ?? '') : undefined,
      )
      handleDialogOpenChange(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSwitchDialogOpenChange = (open: boolean) => {
    setIsSwitchDialogOpen(open)
    if (!open) {
      setPendingSwitchViewId(null)
      setIsSwitching(false)
    }
  }

  const handleDeleteDialogOpenChange = (open: boolean) => {
    if (!open) {
      setPendingDeleteViewId(null)
      setIsDeleting(false)
    }
  }

  const applyViewById = async (viewId: string) => {
    const view = views.find(item => item.id === viewId)
    if (!view) return
    setIsApplyingView(true)
    try {
      // Apply config first to update UI state, then update active view ID
      applyViewQuery(view)
      await applyConfig(view.config)
      await setActiveViewId(viewId)
    } finally {
      setIsApplyingView(false)
    }
  }

  const handleSelectView = async (viewId: string) => {
    if (viewId === activeViewId) {
      return
    }
    if (isModified) {
      setPendingSwitchViewId(viewId)
      setIsSwitchDialogOpen(true)
      return
    }
    await applyViewById(viewId)
  }

  const handleSaveAndSwitch = async () => {
    if (!pendingSwitchViewId) return
    const nextId = pendingSwitchViewId
    const targetView = views.find(item => item.id === nextId)
    if (!targetView) return

    setIsSwitching(true)
    setIsApplyingView(true)
    try {
      // Save current changes first
      if (currentConfig) {
        await saveActiveView(
          currentConfig,
          onApplyQuery ? (currentQuery ?? '') : undefined,
        )
      }
      // Apply new config first to update UI, then update active view ID
      applyViewQuery(targetView)
      await applyConfig(targetView.config)
      await setActiveViewId(nextId)
    } finally {
      setIsApplyingView(false)
      handleSwitchDialogOpenChange(false)
    }
  }

  const handleDiscardAndSwitch = async () => {
    if (!pendingSwitchViewId) return
    const nextId = pendingSwitchViewId
    const targetView = views.find(item => item.id === nextId)
    if (!targetView) return

    setIsSwitching(true)
    setIsApplyingView(true)
    try {
      // Apply new config first to update UI, then update active view ID
      applyViewQuery(targetView)
      await applyConfig(targetView.config)
      await setActiveViewId(nextId)
    } finally {
      setIsApplyingView(false)
      handleSwitchDialogOpenChange(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDeleteViewId) return
    setIsDeleting(true)
    try {
      await deleteView(pendingDeleteViewId)
    } finally {
      handleDeleteDialogOpenChange(false)
    }
  }
  const pendingDeleteView = views.find(view => view.id === pendingDeleteViewId)

  const everyoneLabel = isProjectScoped
    ? 'everyone on this project'
    : isPersonal
      ? 'everyone in your space'
      : 'everyone in your organization'
  const dialogDescription =
    pendingScope === PreferenceScopeEnum.ORGANIZATION
      ? `Saves the current filters, columns, and grouping for ${everyoneLabel}.`
      : 'Saves the current filters, columns, and grouping. Only you can see it.'

  const [open, setOpen] = useState(false)

  const openCreateDialog = (scope: PreferenceScopeEnum) => {
    handleOpenCreateDialog(scope)
    setOpen(false)
  }

  /** Icon-only action that is revealed when its row is hovered or focused. */
  const RowAction = ({
    label,
    onClick,
    Icon,
    destructive = false,
    alwaysVisible = false,
    disabled = false,
  }: {
    label: string
    onClick: () => void
    Icon: typeof StarIcon
    destructive?: boolean
    alwaysVisible?: boolean
    disabled?: boolean
  }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation()
            onClick()
          }}
          className={cn(
            'transition-tonal flex h-7 w-7 shrink-0 items-center justify-center rounded-md duration-150',
            !alwaysVisible &&
              'opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100',
            destructive
              ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          <Icon className="h-4 w-4" weight="regular" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )

  const ViewRow = ({
    view,
    isActive,
    isDefault,
  }: {
    view: { id: string; name: string; scope: PreferenceScopeEnum }
    isActive: boolean
    isDefault: boolean
  }) => {
    const isShared = view.scope === PreferenceScopeEnum.ORGANIZATION
    const Icon = isShared ? UsersIcon : UserIcon
    const canSetDefault = isShared && canManage && !isDefault

    return (
      <div
        className={cn(
          'group flex items-center gap-0.5 rounded-lg pr-1 transition-colors duration-150',
          'hover:bg-accent/50',
          isActive && 'bg-accent/30',
        )}
      >
        <button
          type="button"
          onClick={() => {
            handleSelectView(view.id)
            if (!isModified) setOpen(false)
          }}
          disabled={isLoading}
          className="flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left disabled:cursor-not-allowed"
        >
          <span
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
              isShared ? 'bg-emerald-500/10' : 'bg-blue-500/10',
            )}
          >
            <Icon
              className={cn(
                'h-3.5 w-3.5',
                isShared ? 'text-emerald-500' : 'text-blue-500',
              )}
              weight="duotone"
            />
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              isActive
                ? 'font-medium text-foreground'
                : 'text-foreground/80 group-hover:text-foreground',
            )}
          >
            {view.name}
          </span>
          {isActive && (
            <CheckIcon
              className="h-4 w-4 shrink-0 text-primary"
              weight="bold"
            />
          )}
        </button>
        {isDefault && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label="Default view"
                className="flex h-7 w-7 shrink-0 items-center justify-center"
              >
                <StarIcon
                  className="h-3.5 w-3.5 text-amber-500"
                  weight="fill"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Default view for {everyoneLabel}
            </TooltipContent>
          </Tooltip>
        )}
        {canSetDefault && (
          <RowAction
            label="Make this the default view"
            Icon={StarIcon}
            disabled={isLoading}
            onClick={() => void handleSetDefault(view)}
          />
        )}
        {canDeleteView(view) && (
          <RowAction
            label="Delete view"
            Icon={TrashIcon}
            destructive
            disabled={isLoading}
            onClick={() => setPendingDeleteViewId(view.id)}
          />
        )}
      </div>
    )
  }

  const SectionLabel = ({ children }: { children: string }) => (
    <div className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium text-muted-foreground">
      {children}
    </div>
  )

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(filterChipTriggerClasses(!!activeView), className)}
          >
            <span className="max-w-[140px] truncate">
              {activeView?.name || 'View'}
            </span>
            {isModified && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                Edited
              </span>
            )}
            <CaretDownIcon className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className={cn(
            'w-72 p-0',
            'border-slate-200/80 shadow-lg dark:border-slate-700/50',
            'bg-white dark:bg-slate-900',
            'overflow-hidden',
          )}
        >
          <TooltipProvider delayDuration={300}>
            <div
              className="p-1.5"
              style={{
                maxHeight: '70vh',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
              }}
              onWheel={e => e.stopPropagation()}
            >
              {/* Unsaved changes — only exists while there is something to save */}
              {activeView && isModified && (
                <div className="mb-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-700/60 dark:bg-amber-900/20">
                  <div className="flex items-center gap-2 text-xs font-medium text-amber-800 dark:text-amber-300">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    <span className="truncate">
                      You changed {activeView.name}
                    </span>
                  </div>
                  {!canSaveActiveView && (
                    <p className="mt-1 text-[11px] leading-snug text-amber-700/90 dark:text-amber-400/90">
                      Only admins can update shared views. Save a copy to keep
                      these changes.
                    </p>
                  )}
                  <div className="mt-2 flex gap-1.5">
                    {canSaveActiveView && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={isLoading || !hasConfig}
                        onClick={() => {
                          void handleSaveCurrent()
                          setOpen(false)
                        }}
                        className="h-7 flex-1 rounded-md px-2 text-xs"
                      >
                        <FloppyDiskIcon className="h-3.5 w-3.5" />
                        Save
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isLoading}
                      onClick={() => void handleRevertChanges()}
                      className="h-7 flex-1 rounded-md px-2 text-xs"
                    >
                      <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />
                      Revert
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isLoading || !hasConfig}
                      onClick={() => openCreateDialog(PreferenceScopeEnum.USER)}
                      className="h-7 flex-1 rounded-md px-2 text-xs"
                    >
                      {canSaveActiveView ? 'Save as…' : 'Save a copy'}
                    </Button>
                  </div>
                </div>
              )}

              {orgViews.length > 0 && (
                <div>
                  <SectionLabel>Shared</SectionLabel>
                  <div className="space-y-0.5">
                    {orgViews.map(view => (
                      <ViewRow
                        key={view.id}
                        view={view}
                        isActive={activeViewId === view.id}
                        isDefault={orgDefaultViewId === view.id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {userViews.length > 0 && (
                <div className={cn(orgViews.length > 0 && 'mt-1.5')}>
                  <SectionLabel>Mine</SectionLabel>
                  <div className="space-y-0.5">
                    {userViews.map(view => (
                      <ViewRow
                        key={view.id}
                        view={view}
                        isActive={activeViewId === view.id}
                        isDefault={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {views.length === 0 && (
                <div className="px-3 py-5 text-center">
                  <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-muted/50">
                    <UsersIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    No saved views yet
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Set up filters and columns, then save them as a view.
                  </p>
                </div>
              )}

              <div
                className={cn(
                  'mt-1.5 flex items-center gap-0.5 border-t border-border/50 pt-1.5',
                  views.length === 0 && 'mt-0 border-t-0 pt-0',
                )}
              >
                <button
                  type="button"
                  onClick={() => openCreateDialog(PreferenceScopeEnum.USER)}
                  disabled={isLoading || !hasConfig}
                  className={cn(
                    'flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 text-left text-sm',
                    'text-foreground/80 transition-colors hover:bg-accent/50 hover:text-foreground',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <PlusIcon className="h-4 w-4" weight="bold" />
                  </span>
                  <span className="truncate">New view from current</span>
                </button>
                <div className="pr-1">
                  <RowAction
                    label="Copy link to this view"
                    Icon={LinkIcon}
                    alwaysVisible
                    disabled={isLoading || !hasConfig}
                    onClick={() => {
                      void handleCopyShareLink()
                      setOpen(false)
                    }}
                  />
                </div>
              </div>
            </div>
          </TooltipProvider>
        </PopoverContent>
      </Popover>

      <DialogPattern
        open={isCreateDialogOpen}
        onOpenChange={handleDialogOpenChange}
        title="New view"
        description={dialogDescription}
        icon={<ScopeIcon className={cn('h-5 w-5', scopeDetails.color)} />}
        iconSize="sm"
        isForm={true}
        onSubmit={handleCreateSubmit}
        isSubmitting={isSaving}
        size="md"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSaving}
              className="h-8 rounded-full px-4 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isNameValid || isSaving}
              className="ml-2 h-8 rounded-full px-4 text-sm"
            >
              Save view
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="view-name">View name</Label>
            <Input
              id="view-name"
              value={viewName}
              onChange={event => setViewName(event.target.value)}
              placeholder="Weekly pipeline"
              autoFocus
            />
          </div>
          {canManage && (
            <div className="space-y-2">
              <Label>Who can see it</Label>
              <SegmentedControl<PreferenceScopeEnum>
                aria-label="Who can see this view"
                fill
                value={pendingScope}
                onValueChange={setPendingScope}
                items={[
                  {
                    value: PreferenceScopeEnum.USER,
                    label: 'Only me',
                    icon: UserIcon,
                  },
                  {
                    value: PreferenceScopeEnum.ORGANIZATION,
                    label: 'Everyone',
                    icon: UsersIcon,
                  },
                ]}
              />
            </div>
          )}
        </div>
      </DialogPattern>

      <DialogPattern
        open={isSwitchDialogOpen}
        onOpenChange={handleSwitchDialogOpenChange}
        title="Unsaved changes"
        description="You have unsaved changes to this view. Would you like to save them before switching?"
        icon={
          <WarningIcon className="h-5 w-5 text-amber-500" weight="duotone" />
        }
        iconSize="sm"
        size="md"
        hideCloseButton
        contentClassName="py-0"
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscardAndSwitch}
              disabled={isSwitching}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Discard
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndSwitch}
              disabled={isSwitching}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Save & switch
            </Button>
          </div>
        }
      >
        <div />
      </DialogPattern>

      <DialogPattern
        open={!!pendingDeleteViewId}
        onOpenChange={handleDeleteDialogOpenChange}
        title="Delete view?"
        description={`Are you sure you want to delete "${pendingDeleteView?.name ?? ''}"? This action cannot be undone.`}
        icon={
          <TrashIcon className="h-5 w-5 text-destructive" weight="duotone" />
        }
        iconSize="sm"
        size="md"
        hideCloseButton
        contentClassName="py-0"
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDeleteDialogOpenChange(false)}
              disabled={isDeleting}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Delete
            </Button>
          </div>
        }
      >
        <div />
      </DialogPattern>
    </>
  )
}
